import {Active} from "@dnd-kit/core"
import {observer} from "mobx-react-lite"
import React, {createRef, RefObject, useCallback, useRef} from "react"
import {useBaseDataDisplayModelContext} from "../../hooks/use-base-data-display-model"
import {getDragAttributeInfo, useDropHandler} from "../../../../hooks/use-drag-drop"
import {useDropHintString} from "../../../../hooks/use-drop-hint-string"
import {useInstanceIdContext} from "../../../../hooks/use-instance-id-context"
import {IDataSet} from "../../../../models/data/data-set"
import {GraphPlace} from "../../../axis-graph-shared"
import {GraphAttrRole} from "../../data-display-types"
import {IBaseLayerModel} from "../../models/base-data-display-content-model"
import {DataConfigurationContext} from "../../hooks/use-data-configuration-context"
import {useDataDisplayLayout} from "../../hooks/use-data-display-layout"
import {DroppableSvg} from "../droppable-svg"
import {Legend} from "./legend"

/*
 * Whether a layer should be given a legend area.
 *
 * Graphs have one layer and it is always visible. Maps have several, and only the visible ones get
 * a legend -- with one addition: a layer whose assigned legend attribute this display cannot honor
 * counts as having one. Its `attributeID` reads "" in that state, because the base configuration
 * filters an unusable assignment out, so testing that alone drops the layer before Legend runs and
 * leaves the attribute the user assigned invisible and unremovable. That is the situation Legend
 * now renders a message for, and it can only do so if the layer gets here.
 */
export function layerHasLegendToShow(layer: IBaseLayerModel): boolean {
  const { dataConfiguration } = layer
  return !!(dataConfiguration.attributeID("legend") || dataConfiguration.legendAttributeIsInoperable) &&
    layer.isVisible
}

interface IMultiLegendProps {
  divElt: HTMLDivElement | null
  onDropAttribute: (place: GraphPlace, dataSet: IDataSet, attrId: string) => void
}

export const MultiLegend = observer(function MultiLegend({divElt, onDropAttribute}: IMultiLegendProps) {
  const dataDisplayModel = useBaseDataDisplayModelContext(),
    layout = useDataDisplayLayout(),
    legendRef = useRef() as React.RefObject<HTMLDivElement>,
    instanceId = useInstanceIdContext(),
    droppableId = `${instanceId}-legend-area-drop`,
    role = 'legend' as GraphAttrRole,
    hintString = useDropHintString({role}),
    isDropAllowed = dataDisplayModel?.placeCanAcceptAttributeIDDrop ?? (() => false),
    divRefs =
      useRef([] as RefObject<HTMLDivElement>[] & { [key: number]: RefObject<HTMLDivElement> }),
    extentsRef = useRef([] as number[])

  const legendBoundsTop = layout?.computedBounds?.legend?.top ?? 0
  const layersWithLegendsArray = Array.from(dataDisplayModel.layers).filter(layerHasLegendToShow)
  const handleIsActive = (active: Active) => {
      const {dataSet, attributeId: droppedAttrId} = getDragAttributeInfo(active) || {}
        return isDropAllowed('legend', dataSet, droppedAttrId)
    },

    setDesiredExtent = useCallback((layerIndex: number, extent: number) => {
      extentsRef.current[layerIndex] = extent
      const totalDesiredExtent = extentsRef.current.reduce((a, b) => a + b, 0)
      layout.setDesiredExtent('legend', totalDesiredExtent)
      const theDivElt = Array.from(divRefs.current)[layerIndex].current
      if (theDivElt) {
        theDivElt.style.height = `${extent}px`
      }
    }, [layout])

  useDropHandler(droppableId, active => {
    const {dataSet, attributeId: dragAttributeID} = getDragAttributeInfo(active) || {}
    dataSet && dragAttributeID && isDropAllowed('legend', dataSet, dragAttributeID) &&
    onDropAttribute('legend', dataSet, dragAttributeID)
  })

  const renderLegends = () => {
    return (
      layersWithLegendsArray
        .map(layer => {
            const
              index = layer.layerIndex,
              divRef = divRefs.current[index] || createRef<HTMLDivElement>()
            divRefs.current[index] = divRef
            return (
              <div className='legend' key={layer.id} ref={divRef} style={{height: `${extentsRef.current[index]}px`}}>
                <DataConfigurationContext.Provider value={layer.dataConfiguration}>
                  <Legend layerIndex={index}
                          setDesiredExtent={setDesiredExtent}
                          onDropAttribute={onDropAttribute}
                  />
                </DataConfigurationContext.Provider>
              </div>
            )
          }
        ))
  }

  return (
    <div ref={legendRef} className='multi-legend'
         style={{top: legendBoundsTop}}>
      {renderLegends()}
      {layersWithLegendsArray.length > 0 &&
        <DroppableSvg
          className="droppable-legend"
          portal={divElt}
          target={legendRef.current}
          dropId={droppableId}
          onIsActive={handleIsActive}
          hintString={hintString}
        />
}
    </div>
  )
})

MultiLegend.displayName = "MultiLegend"
